import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { ExtendedSelectOption } from '../../../@types/shared';
import Text from '../../atoms/Text';

import CustomTooltipInfo from '../../atoms/CustomTooltipInfo';
import SelectionContainer, {
  SelectionOption,
} from '../../../containers/SelectionContainer';

import styles from './OptionSelect.module.scss';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'size' | 'onChange'> {
  size?: 'md' | 'sm';
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  tooltip?: string;
  multiple?: boolean;
  value?: ExtendedSelectOption['id'] | Array<ExtendedSelectOption['id']>;
  onChange: (value: string | string[] | undefined) => void;
  options: ExtendedSelectOption[];
  helperText?: string;
  errorText?: string;
}

/**
 * <OptionSelect> component
 */
export const OptionSelect = ({
  id,
  className,
  loading,
  value,
  label,
  tooltip,
  size,
  options,
  helperText,
  errorText,
  onChange,
  multiple = false,
  disabled = false,
  ...props
}: React.PropsWithChildren<Props>) => {
  const inputId = id ?? uuid();

  /**
   * Handle change to selection when popover is open
   */
  const handleOptionSelect = React.useCallback(
    (v: string | string[] | number | number[] | undefined) => {
      if (!v) {
        onChange(undefined);
      } else {
        onChange(v as string & string[]);
      }
    },
    [onChange],
  );

  const cls = React.useMemo(
    () =>
      classNames(styles.wrapper, className, {
        disabled,
        error: !!errorText,
        'size-sm': size === 'sm',
      }),
    [className, disabled, errorText, size],
  );

  return (
    <div className={cls} {...props}>
      {label && (
        <div className="d-flex gap-1 text-items-center mb-2">
          <Text as="label" htmlFor={inputId} weight="bold">
            {label}
          </Text>
          {tooltip && <CustomTooltipInfo info={tooltip} />}
        </div>
      )}

      <SelectionContainer
        id={inputId}
        className={classNames(styles.container)}
        value={value}
        gap={0.5}
        aria-label={label}
        aria-disabled={disabled}
        onChange={handleOptionSelect}
        allowMultiple={multiple as boolean}
      >
        {options.map((opt) => (
          <SelectionOption
            className={classNames(styles.option, opt.className, {
              [`bg-${opt.backgroundColor}`]: !!opt.backgroundColor,
            })}
            key={opt.id}
            value={opt.id}
            disabled={disabled}
            aria-label={opt.label}
            aria-disabled={disabled}
          >
            <Text weight="bold" color={opt.color}>
              {opt.label}
            </Text>
          </SelectionOption>
        ))}
      </SelectionContainer>

      {helperText && !errorText && (
        <Text as="span" className="mt-2" size="sm1">
          {helperText}
        </Text>
      )}
      {errorText && (
        <Text as="span" className="mt-2" color="red400" size="sm1">
          {errorText}
        </Text>
      )}
    </div>
  );
};

export default OptionSelect;
